import React, { useState, useEffect, useRef, useCallback } from "react";
import env from "@beam-australia/react-env";

import { BlockUI } from "primereact/blockui";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Toast } from "primereact/toast";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";

import { useTranslation } from "react-i18next";

import { useDebounce } from "../../../../hooks/useDebounce";
import useCRUDService from "../../../../hooks/useCRUDService";
import useApi from "../../../../hooks/useApi";
import ActiveInactive from "../../../../components/activeInactive/activeInactive";
import BeneficiariesForm from "./beneficiariesForm";
import Layout from "../../../../layout/layout";
import Header from "../../../../layout/header";
import useSkeleton from "../../../../hooks/useSkeleton";

import { atom, useAtom } from "jotai";
import { loadingState } from "../../../../states/loadingState";
import { beneficiaryIdState } from "../../../../states/beneficiaryIdState";
import BeneficiaryPaymentDialog from "./beneficiaryPaymentsDialog";
import BeneficiaryTimeline from "./beneficiaryTimeline";

const Beneficiaries = () => {
  const [isLoading, setIsLoading] = useAtom(loadingState);

  const setBeneficiaryId = useAtom(beneficiaryIdState);

  const s = useSkeleton("commons");
  const api = useApi();
  const { t } = useTranslation();
  const CRUDService = useCRUDService("beneficiaries");

  const [entities, setEntities] = useState(null);
  const [formDialog, setFormDialog] = useState(false);
  const [beneficiaryPaymentDialog, setBeneficiaryPaymentDialog] =
    useState(false);

  const [expandedRows, setExpandedRows] = useState(null);

  const [first, setFirst] = useState(0);
  const [rows, setRows] = useState(100);
  const [sortField, setSortField] = useState(null);
  const [sortOrder, setSortOrder] = useState(null);
  const [globalFilter, setGlobalFilter] = useState("");

  const [totalRecords, setTotalRecords] = useState(0);

  const searchInput = useRef(null);

  // Inicializamos el debounce para la búsqueda
  const debouncedSearchTerm = useDebounce(globalFilter, 2000);

  const toast = useRef(null);
  const dt = useRef(null);

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      const columns = JSON.stringify([
        {
          name: "name",
          type: "string",
        },
        {
          name: "lastName",
          type: "string",
        },
        {
          name: "documentNumber",
          type: "string",
        },
        {
          name: "phone",
          type: "string",
        },
        {
          name: "address",
          type: "string",
        },
        {
          name: "notes",
          type: "string",
        },
        {
          name: "active",
          type: "boolean",
        },
      ]);

      const response = await CRUDService.getEntities(
        columns,
        first,
        rows,
        sortField ?? "name",
        sortOrder >= 0 ? "ASC" : "DESC",
        debouncedSearchTerm
      );

      setTotalRecords(response.data.totalRows);
      setEntities(response.data.rows);
    } catch (error) {
      toast.current.show({
        severity: "error",
        summary: "Error",
        detail: t("errors.general"),
        life: 3000,
      });
    } finally {
      setIsLoading(false);
    }
  }, [first, rows, sortField, sortOrder, debouncedSearchTerm, CRUDService]);

  useEffect(() => {
    fetchData();
  }, [first, rows, sortField, sortOrder, debouncedSearchTerm]);

  const openFormDialog = async (formMode, selectedBeneficiaries) => {
    setFormSettings((prev) => ({
      ...prev,
      formMode: formMode,
      entityId: selectedBeneficiaries?.id,
      hideDialog: hideDialog,
      CRUDService: CRUDService,
    }));

    setFormDialog(true);
  };

  const openBeneficiaryPaymentDialog = async (selectedBeneficiaries) => {
    setBeneficiaryId(selectedBeneficiaries?.id);

    setBeneficiaryPaymentDialog(true);
  };

  const hideDialog = (action) => {
    if (action !== "CANCEL") {
      setFormDialog(false);
      // Restablece todos los filtros a sus valores predeterminados
      setFirst(0);
      setRows(100);
      setSortField(null);
      setSortOrder(null);
      setGlobalFilter("");
      // Restablece el valor del campo de entrada
      if (searchInput.current) {
        searchInput.current.value = "";
      }
      // Luego, realiza una nueva búsqueda con los filtros restablecidos
      fetchData();
    }

    setFormDialog(false);
  };

  const exportCSV = () => {
    dt.current.exportCSV();
  };

  const [formSettings, setFormSettings] = useState({
    entityId: -1,
    formMode: "",
    hideDialog: hideDialog,
    CRUDService: CRUDService,
  });

  const actions = (
    <React.Fragment>
      <Button
        label={t("DataTable.New")}
        icon="pi pi-plus"
        className="p-button-primary"
        onClick={() => openFormDialog("CREATE")}
      />
    </React.Fragment>
  );

  const activeInactiveItem = async (id) => {
    setIsLoading(true);

    try {
      const responseActiveToggle = await api.put(
        `${env("API_URL")}/beneficiaries/toggleactive/${id}`
      );

      toast.current.show({
        severity: "success",
        summary: t("forms.messages.titles.success"),
        detail: t("forms.messages.operation.success"),
        life: 3000,
      });
    } catch (error) {
      toast.current.show({
        severity: "error",
        summary: t("forms.messages.titles.error"),
        detail: t("forms.messages.operation.error"),
        life: 3000,
      });
      throw new Error();
    } finally {
      setIsLoading(false);
    }
  };

  const actionActive = (rowData) => {
    return (
      <ActiveInactive
        onChange={activeInactiveItem}
        itemId={rowData.id}
        value={rowData.active}
      />
    );
  };

  const actionBodyTemplate = (rowData) => {
    return (
      <div className="flex justify-content-center">
        <Button
          icon="pi pi-history"
          className="p-button-rounded p-button-info mr-2"
          tooltip="Línea de tiempo"
          tooltipOptions={{ position: "top" }}
          onClick={() =>
            setExpandedRows((prev) => {
              const key = rowData.id;
              if (prev && prev[key]) {
                const next = { ...prev };
                delete next[key];
                return Object.keys(next).length ? next : null;
              }
              return { ...(prev ?? {}), [key]: true };
            })
          }
        />
        <Button
          icon="pi pi-dollar"
          className="p-button-rounded p-button-primary mr-2"
          onClick={() => openBeneficiaryPaymentDialog(rowData)}
        />
        <Button
          icon="pi pi-pencil"
          className="p-button-rounded p-button-success mr-2"
          onClick={() => openFormDialog("UPDATE", rowData)}
        />
        <Button
          icon="pi pi-trash"
          className="  p-button-rounded p-button-danger"
          onClick={() => openFormDialog("DELETE", rowData)}
        />
      </div>
    );
  };

  const header = (
    <div className="table-header">
      <span className="p-input-icon-left">
        <i className="pi pi-search" />
        <InputText
          ref={searchInput}
          type="search"
          onInput={(e) => setGlobalFilter(e.target.value)}
          placeholder={`${t("DataTable.Search")}...`}
        />
      </span>
      <Button
        outlined
        label={t("DataTable.Export")}
        icon="pi pi-upload"
        className="p-button-secondary"
        onClick={exportCSV}
        style={{
          marginLeft: "20px",
        }}
      />
    </div>
  );

  const getDialogTitle = () => {
    let action = "Add";

    if (formSettings.formMode == "DELETE") {
      action = "Delete";
    } else if (formSettings.formMode == "UPDATE") {
      action = "Edit";
    }

    return `${t(action)} ${t("entities.beneficiaries.singular")}`;
  };

  const getDialogHeader = () => {
    if (formSettings.formMode === "DELETE") {
      return getDialogTitle();
    }

    return (
      <div className="abm-form-hero">
        <h2 className="abm-form-hero-title">{getDialogTitle()}</h2>
        <p className="abm-form-hero-subtitle">
          Complete la información para gestionar el registro.
        </p>
      </div>
    );
  };

  return (
    <Layout
      header={
        <Header
          icon={<i className="pi pi-users"></i>}
          title={t("entities.beneficiaries.plural")}
          actions={actions}
        />
      }
    >
      <Toast ref={toast} />
      <div className={s.get("dataTableContainer")}>
        <DataTable
          onPage={(e) => {
            setFirst(e.first);
            setRows(e.rows);
          }}
          onSort={(e) => {
            setSortField(e.sortField);
            setSortOrder(e.sortOrder);
          }}
          sortField={sortField}
          sortOrder={sortOrder}
          ref={dt}
          value={entities}
          lazy
          paginator
          first={first}
          rows={rows}
          totalRecords={totalRecords}
          columnResizeMode="fit"
          showGridlines
          dataKey="id"
          rowsPerPageOptions={[10, 25, 100, 1000]}
          paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
          currentPageReportTemplate={`${t(
            "DataTable.currentPageReportTemplate"
          )} ${t("Beneficiaries")}`}
          globalFilter={globalFilter}
          header={header}
          className="flex flex-column w-full"
          expandedRows={expandedRows}
          onRowToggle={(e) => setExpandedRows(e.data)}
          rowExpansionTemplate={(rowData) => (
            <BeneficiaryTimeline documentNumber={rowData.documentNumber} />
          )}
        >
          <Column
            field="name"
            header={t("entities.beneficiaries.name")}
            sortable
          ></Column>
          <Column
            field="lastName"
            header={t("entities.beneficiaries.lastName")}
            sortable
          ></Column>
          <Column
            field="documentNumber"
            header={t("entities.beneficiaries.documentNumber")}
            sortable
          ></Column>
          <Column
            field="phone"
            header={t("entities.beneficiaries.phone")}
            sortable
          ></Column>
          <Column
            field="address"
            header={t("entities.beneficiaries.address")}
            sortable
          ></Column>
          <Column
            field="notes"
            header={t("entities.beneficiaries.notes")}
            sortable
          ></Column>
          <Column
            field="active"
            header={t("entities.beneficiaries.active")}
            style={{ textAlign: "center" }}
            sortable
            body={actionActive}
            exportable={false}
          ></Column>

          <Column body={actionBodyTemplate} exportable={false}></Column>
        </DataTable>
        <React.Fragment>
          <Dialog
            visible={formDialog}
            header={getDialogHeader()}
            modal
            className="flex w-9"
            onHide={hideDialog}
          >
            <BeneficiariesForm settings={formSettings} />
          </Dialog>
        </React.Fragment>
        <React.Fragment>
          <Dialog
            visible={beneficiaryPaymentDialog}
            modal
            className="flex w-9"
            onHide={() => setBeneficiaryPaymentDialog(false)}
          >
            <BeneficiaryPaymentDialog />
          </Dialog>
        </React.Fragment>
      </div>
    </Layout>
  );
};

export default Beneficiaries;
